// <SharedLiveVideo> — read-only subscriber to the live-stream registry.
//
// Used by the focus modal's hero so it shows the same WHEP MediaStream
// the underlying tile is already publishing. No new RTCPeerConnection,
// no second AVA session, no flicker on modal open/close.
//
// If the tile hasn't published yet (e.g. tile scrolled out, offline,
// session connecting), the parent should render a fallback (snapshot).

import { useEffect, useRef, useState } from "react";
import { getStream, subscribe } from "./liveStreamRegistry";

export interface SharedLiveVideoProps {
  deviceId: string;
  className?: string;
}

export function SharedLiveVideo({ deviceId, className }: SharedLiveVideoProps): JSX.Element | null {
  const [stream, setStream] = useState<MediaStream | null>(() => getStream(deviceId));
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    setStream(getStream(deviceId));
    return subscribe(deviceId, (next) => {
      setStream(next);
    });
  }, [deviceId]);

  // Bind the stream to <video>.srcObject imperatively. React doesn't
  // know how to diff srcObject (it's not an attribute), so we always
  // assign and only call .play() when the stream identity changes.
  useEffect(() => {
    const node = videoRef.current;
    if (!node) return;
    if (node.srcObject !== stream) {
      try {
        node.srcObject = stream;
      } catch {
        /* ignore */
      }
    }
    if (stream) {
      const p = node.play();
      if (p) p.catch(() => {});
    }
  }, [stream]);

  if (!stream) return null;
  return (
    <video
      ref={videoRef}
      className={className}
      playsInline
      muted
      autoPlay
      aria-hidden="true"
    />
  );
}
